import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface ArtifactCardShellProps {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  versionLabel?: string | null;
  onClick: () => void;
  /** Hover-revealed action buttons/icons on the right side of the card. */
  actions?: ReactNode;
}

/**
 * Shared presentational shell for inline chat artifact cards — the clickable
 * container, icon box, title/version-badge row, and hover-revealed action
 * row are identical between `HtmlArtifactCard` and `ArtifactCard` (docx,
 * pptx, xlsx, pdf, etc.); only the icon, copy, and click/action behavior
 * differ per artifact type.
 */
export function ArtifactCardShell({
  icon: Icon,
  title,
  subtitle,
  versionLabel,
  onClick,
  actions,
}: Readonly<ArtifactCardShellProps>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group mt-3 flex items-center gap-3 p-3 rounded-lg border border-border-secondary bg-surface-secondary hover:bg-surface-tertiary hover:border-brand-primary/30 cursor-pointer transition-all max-w-sm text-left"
    >
      <div className="w-10 h-10 rounded-lg bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-brand-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-content-primary truncate">{title}</span>
          {versionLabel && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary font-medium">
              {versionLabel}
            </span>
          )}
        </div>
        <span className="text-xs text-content-tertiary">{subtitle}</span>
      </div>
      {actions && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">{actions}</div>
      )}
    </button>
  );
}
